import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Copy, ExternalLink, QrCode, RefreshCw, Share2, X } from 'lucide-react';
import toast from 'react-hot-toast';
import QRCode from 'qrcode';
import { useAppSelector } from '@/app/hooks';
import { Button, IconButton } from '@/components/ui';
import { onPush } from '@/features/notifications/push';
import { formatCurrency } from '@/utils/formatters';

interface Props {
    isOpen: boolean;
    paymentUrl: string;
    /** Raw EMV QRIS payload; drawn as a QR when non-blank, otherwise the hosted-page link-out is shown */
    qrString?: string | null;
    /** invoiceIdentifier: a TRANSACTION push with this targetId marks it paid */
    invoiceIdentifier?: string;
    /** Optional poll; resolve true when paid. Called every 5s and on "Check status". */
    poll?: () => Promise<boolean>;
    onPaid?: () => void;
    onClose: () => void;
    /** Amount shown in the hero card */
    amount?: number;
    /** Method name for the title (default QRIS) */
    methodName?: string;
    /** Landscape-only left column (order summary) */
    summary?: React.ReactNode;
}

/**
 * 07 — hosted payment (QRIS / VA) waiting screen. Payment pages block iframes, so we link out and
 * watch for completion via push and/or polling. Full-screen on both form factors.
 */
export const PendingPaymentView: React.FC<Props> = ({ isOpen, paymentUrl, qrString, invoiceIdentifier, poll, onPaid, onClose, amount, methodName, summary }) => {
    const { t } = useTranslation();
    const shopName = useAppSelector((s) => s.auth.selectedShop?.shopName);
    const [paid, setPaid] = useState(false);
    const [checking, setChecking] = useState(false);
    const qr = qrString?.trim() || '';
    const canvasRef = useRef<HTMLCanvasElement>(null);

    useEffect(() => {
        if (!isOpen || !qr || !canvasRef.current) return;
        QRCode.toCanvas(canvasRef.current, qr, { errorCorrectionLevel: 'M', margin: 1, width: 260 }).catch(() => { /* fallback link stays visible */ });
    }, [isOpen, qr]);

    useEffect(() => {
        if (!isOpen) { setPaid(false); return; }
        let stop = false;
        const markPaid = () => {
            if (stop || paid) return;
            setPaid(true);
            toast.success(t('checkout.paymentReceived'));
            onPaid?.();
        };
        const offPush = onPush((p) => {
            if (p.target === 'TRANSACTION' && invoiceIdentifier && p.targetId === invoiceIdentifier) markPaid();
        });
        let timer: number | undefined;
        if (poll) {
            const tick = async () => {
                if (stop) return;
                try { if (await poll()) { markPaid(); return; } } catch { /* keep polling */ }
                timer = window.setTimeout(tick, 5000);
            };
            timer = window.setTimeout(tick, 5000);
        }
        return () => { stop = true; offPush(); if (timer) clearTimeout(timer); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, invoiceIdentifier, paymentUrl]);

    const copy = async () => {
        try { await navigator.clipboard.writeText(paymentUrl); toast.success(t('checkout.linkCopied')); } catch { /* ignore */ }
    };
    const share = async () => {
        if (navigator.share) { try { await navigator.share({ url: paymentUrl }); } catch { /* cancelled */ } } else copy();
    };
    const checkNow = async () => {
        if (!poll) return;
        setChecking(true);
        try {
            if (await poll()) { setPaid(true); toast.success(t('checkout.paymentReceived')); onPaid?.(); }
            else toast(t('checkout.stillPending'));
        } catch { toast.error(t('common.error')); }
        setChecking(false);
    };

    if (!isOpen) return null;
    const title = paid ? t('checkout.paymentSuccessful') : methodName ? t('checkout.payWith', { method: methodName }) : t('checkout.qrisTitle');

    return createPortal(
        <div className="fixed inset-0 z-[850] flex bg-surface-page text-ink-primary">
            {summary && (
                <aside className="hidden w-[420px] shrink-0 flex-col gap-[18px] border-r border-line-subtle bg-surface-card p-6 lg:flex">
                    <div className="flex items-center gap-3">
                        <IconButton variant="sunken" label={t('common.close')} onClick={onClose}><X /></IconButton>
                        <h1 className="text-[22px] font-black">{title}</h1>
                    </div>
                    {summary}
                    <div className="flex-1" />
                    <div className="flex items-center gap-3.5 rounded-lg bg-surface-sunken p-4">
                        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-accentsoft"><span className="h-3 w-3 rounded-full bg-accent-500 animate-pulse-ring" /></span>
                        <span className="flex flex-col"><span className="text-[15px] font-extrabold">{t('checkout.waitingPayment')}</span><span className="text-[13px] text-ink-muted">{t('checkout.statusAuto')}</span></span>
                    </div>
                    <div className="grid grid-cols-2 gap-2.5">
                        <Button variant="ghost" size="lg" fullWidth onClick={onClose}>{paid ? t('checkout.newSale') : t('common.close')}</Button>
                        <Button variant="outline" size="lg" fullWidth icon={<RefreshCw />} onClick={checkNow} loading={checking} disabled={!poll || paid}>{t('checkout.checkStatus')}</Button>
                    </div>
                </aside>
            )}
            <div className="flex min-w-0 flex-1 flex-col">
                <header className={`flex shrink-0 items-center gap-2.5 px-5 pb-3 pt-2 ${summary ? 'lg:hidden' : 'lg:px-7 lg:pt-[22px]'}`}>
                    <IconButton variant="card" label={t('common.close')} onClick={onClose}><X /></IconButton>
                    <h1 className="flex-1 text-xl font-black lg:text-[22px]">{title}</h1>
                </header>
                <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto px-5 pb-6 lg:items-center lg:justify-center lg:px-7">
                    <div className="flex flex-col items-center gap-1.5 rounded-2xl bg-surface-card p-6 shadow-md lg:w-[460px] lg:bg-surface-brand lg:p-[32px_48px] lg:text-white lg:shadow-brand">
                        {shopName && <span className="text-[13px] font-bold text-ink-muted lg:text-primary-100">{shopName}</span>}
                        {amount != null && <span className="text-[36px] font-black tnum tracking-[-0.02em] text-ink-brand lg:text-[44px] lg:text-white">{formatCurrency(amount)}</span>}
                        <div className={`mt-3.5 flex w-full max-w-[300px] flex-col items-center gap-3 rounded-lg bg-surface-sunken p-5 text-ink-muted ${qr ? '' : 'border-2 border-dashed border-line'}`}>
                            {qr ? (
                                <>
                                    <canvas ref={canvasRef} className="rounded-md bg-white" aria-label={t('checkout.scanToPay')} />
                                    <span className="text-[13px] font-bold">{t('checkout.scanToPay')}</span>
                                </>
                            ) : (
                                <>
                                    <QrCode className="h-12 w-12" />
                                    <span className="text-[13px] font-bold">{t('checkout.dynamicCode')}</span>
                                </>
                            )}
                            <Button variant="primary" size="md" fullWidth icon={<ExternalLink />} onClick={() => window.open(paymentUrl, '_blank', 'noopener')} disabled={paid}>
                                {t('checkout.openPayment')}
                            </Button>
                            <div className="flex gap-2">
                                <Button variant="ghost" size="sm" icon={<Copy />} onClick={copy}>{t('common.copy')}</Button>
                                <Button variant="ghost" size="sm" icon={<Share2 />} onClick={share}>{t('common.share')}</Button>
                            </div>
                        </div>
                        <a href={paymentUrl} target="_blank" rel="noopener noreferrer" className="mt-2 max-w-full truncate text-xs text-ink-muted underline lg:text-primary-100">{paymentUrl}</a>
                    </div>
                    {!paid ? (
                        <div className={`flex items-center gap-3.5 rounded-lg border border-line-subtle bg-surface-card p-4 ${summary ? 'lg:hidden' : 'lg:w-[460px]'}`}>
                            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-accentsoft"><span className="h-3 w-3 rounded-full bg-accent-500 animate-pulse-ring" /></span>
                            <span className="flex flex-col"><span className="text-[15px] font-extrabold">{t('checkout.waitingPayment')}…</span><span className="text-[13px] text-ink-muted">{t('checkout.statusAuto')}</span></span>
                        </div>
                    ) : (
                        <div className={`rounded-lg bg-status-success-soft p-4 text-center text-[15px] font-extrabold text-success-700 ${summary ? 'lg:hidden' : 'lg:w-[460px]'}`}>{t('checkout.paymentReceived')}</div>
                    )}
                </div>
                <footer className={`flex shrink-0 flex-col gap-2.5 px-5 pb-[max(26px,env(safe-area-inset-bottom))] ${summary ? 'lg:hidden' : 'lg:items-center lg:px-7'}`}>
                    <Button variant="outline" size="lg" fullWidth icon={<RefreshCw />} onClick={checkNow} loading={checking} disabled={!poll || paid} className="lg:max-w-[460px]">{t('checkout.checkStatus')}</Button>
                    <Button variant={paid ? 'primary' : 'ghost'} size="md" fullWidth onClick={onClose} className="lg:max-w-[460px]">{paid ? t('checkout.newSale') : t('common.close')}</Button>
                </footer>
            </div>
        </div>,
        document.body,
    );
};

export default PendingPaymentView;
